import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { formatDateTime, formatCurrency } from '@hellenica/shared';
import type { Booking } from '@hellenica/shared';

export default function BookingList() {
  const { data, isLoading } = useQuery<{ bookings: Booking[] }>({
    queryKey: ['bookings'],
    queryFn: () => api.get('/v1/admin/bookings'),
  });

  if (isLoading) return <div className="animate-pulse">Loading...</div>;

  const statusColors: Record<string, string> = {
    inquiry: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    deposit_paid: 'bg-indigo-100 text-indigo-800',
    fully_paid: 'bg-green-100 text-green-800',
    completed: 'bg-teal-100 text-teal-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">Bookings</h1>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.bookings.map((booking) => (
              <tr key={booking.id}>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{booking.customer_name}</div>
                  <div className="text-sm text-gray-500">{booking.customer_email}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {booking.appointment_datetime ? formatDateTime(booking.appointment_datetime) : '-'}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${statusColors[booking.status]}`}>
                    {booking.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {booking.total_amount ? formatCurrency(booking.total_amount) : '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  <Link to={`/bookings/${booking.id}`} className="text-brand-bronze hover:underline text-sm">
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
